import React, { useCallback } from "react";
import PropTypes from "prop-types";

import DialogContent from "./DialogContent";
import "./Dialog.scss";

/**
 * Dialog
 * @module
 * @param {bool} isOpen - Is dialog open?
 * @param {func} onClose - Function invoked on dialog close
 * @param {func} [onCancel] - Function invoked on cancel
 * @param {func} [onConfirm] - Function invoked on confirm
 * @param {element} children - Children content of dialog
 * @param {string} title - Title text of dialog
 * @param {bool} [fullscreen] - Full screen dialog
 * @param {bool} [hideButtons] - Hide dialog buttons
 * @param {bool} [hideCancelBtn] - Hide cancel button
 * @param {bool} [confirmDisabled] - Confirm disabled
 * @param {string} [form] - Form
 * @param {string} [className] - Dialog class name
 * @param {func} [onEsc] - Invoked when user presses esc
 * @param {string} [confirmText] - Text of confirm button
 * @param {string} [cancelText] - Text of cancel button
 * @param {number} relativeLeft - Window relative left
 * @param {number} relativeTop - Window relative top
 * @param {bool} disableConfirmOnEnter - Disabled confim on Enter
 */
const DialogComponent = (props) => {
  const { isOpen, onClose, onEsc } = props
  const handleKeyDown = async (e) => {
    if (e.key === "Enter" && document.activeElement.tagName.toLowerCase() !== 'textarea' && props.onConfirm) {
      props.onConfirm();
    }
  };

  const handleClose = useCallback((event) => {
    if (event?.nativeEvent.keyCode === 27) {
      if (onEsc) {
        onEsc();
      }
    }
    onClose();
  }, [onEsc, onClose]);

  const className = `k-dialog-content rt-dialog ${props.fullscreen ? "fullscreen" : ""} ${props.className || ""}`;
  return (
    <>
      {isOpen && (
        <div
          className="k-dialog-wrapper" onDoubleClick={(e) => e.stopPropagation()}
        >
          <div onKeyDown={props.disableConfirmOnEnter ? undefined : handleKeyDown}>
            <DialogContent
              title={props.title}
              onClose={handleClose}
              onCancel={props.onCancel}
              children={props.children}
              className={className}
              fullscreen={props.fullscreen}
              form={props.form}
              onClick={props.onConfirm}
              disabled={props.confirmDisabled}
              cancelDisabled={props.cancelDisabled}
              width={props.width}
              confirmText={props.confirmText}
              cancelText={props.cancelText}
              hideButtons={props.hideButtons}
              hideCancelBtn={props.hideCancelBtn}
              relativeTop={props.relativeTop || 0}
              relativeLeft={props.relativeLeft || 0}
              level={props.level}
              onMiddleClick={props.onMiddleClick}
              middleText={props.middleText}
            />
          </div>
        </div>
      )}
    </>
  );
};

DialogComponent.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onMiddleClick: PropTypes.func,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  children: PropTypes.element.isRequired,
  title: PropTypes.string.isRequired,
  fullscreen: PropTypes.bool,
  hideButtons: PropTypes.bool,
  hideCancelBtn: PropTypes.bool,
  confirmDisabled: PropTypes.bool,
  form: PropTypes.string,
  className: PropTypes.string,
  disableConfirmOnEnter: PropTypes.bool,
  onEsc: PropTypes.func,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  middleText: PropTypes.string,
  relativeTop: PropTypes.number,
  relativeLeft: PropTypes.number,
  level: PropTypes.number,
};

export default DialogComponent;
